import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Col, Row, VertButton, VertImage, VertTypography } from '@vertem-core/vertice-ds';

import generateUUID from 'Utils/generateUUID';
import { pointsMask } from 'Utils/commons/points';
import { limitString } from 'Utils/commons/string';
import { OrderStatus } from './OrderStatus';
import { useRating } from 'components/RatingProducts/Provider';
import RatingProducts from 'components/RatingProducts';
import useSupplier from 'Hooks/useSupplier';
import ynToBool from 'Utils/ynToBool';

export const OrderItemDesktop = ({
  order,
  ratingModalOpen,
  handleChangeModal,
  handleOpenModalTracking,
  handleChangeSkuTarget,
  skuTargetForRating,
  checkIfOrderWas10DaysAgo,
}) => {
  const { vendorOrderModel, isVirtual } = order;
  const { supplierWithoutShipping } = useSupplier(vendorOrderModel);
  const AvailableVendors = useSelector(({ AvailableVendors: data }) => data);
  const Catalog = useSelector(({ Catalog: data }) => data);
  const { isEnabled, filterProductsWithoutRating, ratingList, configRating, isLoading } = useRating();
  const navigate = useNavigate();

  const {
    currencyConfiguration: { decimalPlaces, pointsName },
  } = Catalog;

  const hasFreeShipping = (vendor) => vendor.totalShippingValue === 0 && 'highlight';

  const formattedShipping = (vendor) =>
    pointsMask(vendor.totalShippingValue, decimalPlaces, pointsName);

  const getVendorName = (vendorId) => {
    return (AvailableVendors?.value?.find((vendor) => vendor.id === vendorId) || {})?.name || '';
  };

  const showRatingProduct = (vendor) => {
    const items =
      isEnabled && vendor && vendor.items && vendor.items.length
        ? filterProductsWithoutRating(
            vendor.items.map(({ imageUrl, name, productSkuId }) => ({
              productImg:
                imageUrl ||
                'https://vertice-ds.s3.amazonaws.com/Assets/Images/fallbacks/fallback-img-store.png',
              productName: name,
              seller: vendor.vendorName,
              orderId: vendor.orderId,
              supplierId: vendor.vendorId,
              productSku: productSkuId,
            })),
          ).filter(({ productSku }) => skuTargetForRating === productSku)
        : [];

    return (
      <RatingProducts
        items={items}
        open={ratingModalOpen}
        onClose={() => handleChangeModal(false)}
      />
    );
  };

  const goToProductDetailsPage = (productSkuId) => {
    if (productSkuId) {
      navigate(`/product/${productSkuId}`);
    }
  };

  return vendorOrderModel.map((vendor) => (
    <Col className='order-item-list col' key={generateUUID()}>
      <Col className='order-vendor-and-shipping col'>
        <VertTypography.Paragraph
          className='order-vendor'
          aria-label={`Fornecido e entregue por ${vendor.vendorName}`}
        >
          Fornecido e entregue por <span>{getVendorName(vendor.vendorId)}</span>
        </VertTypography.Paragraph>
        <VertTypography.Paragraph
          className={`order-shipping ${hasFreeShipping(vendor)}`}
          style={{
            display: ynToBool(process.env.REACT_APP_HIDE_FREIGHT) ? 'none' : '',
          }}
        >
          Frete:{' '}
          <span>
            {hasFreeShipping(vendor)
              ? 'Grátis'
              : `${formattedShipping(vendor).value} ${formattedShipping(vendor).label}`}
          </span>
        </VertTypography.Paragraph>
      </Col>

      {vendor.items.map((item) => {
        const formattedItemValue = pointsMask(item?.sellingPrice || 0, decimalPlaces, pointsName);
        const hasFreeItem = formattedItemValue.value == 0;

        return (
          <>
            <Row className='order-item' align='middle' gutter={6} key={generateUUID()}>
              <Col className='item-info col-5'>
                <VertImage
                  rootClassName='item-image'
                  alt={item.name}
                  src={item.imageUrl}
                  placeholder='https://vertice-ds.s3.amazonaws.com/Assets/Images/fallbacks/fallback-img-store.png'
                  fallback='https://vertice-ds.s3.amazonaws.com/Assets/Images/fallbacks/fallback-img-store.png'
                />
                <Col className='col'>
                  <VertTypography.Paragraph className='item-name'>
                    {limitString(item.name)}
                  </VertTypography.Paragraph>
                  <OrderStatus status={vendor?.status} />
                  <VertTypography.Paragraph className='item-quantity padding-bottom-extra-small'>
                    Itens({item.quantity})
                  </VertTypography.Paragraph>
                  {isEnabled &&
                    ratingList[item?.productSkuId] &&
                    configRating.minimumRatingQuantity <= ratingList[item?.productSkuId].rating && (
                      <>
                        <SmallRate rate={ratingList[item?.productSkuId].rating} total={5} />
                      </>
                    )}
                </Col>
              </Col>
              <Col className='item-value col'>
                <VertTypography.Paragraph>
                  <span className={`${hasFreeItem ? 'color-highlight' : ''}`}>
                    {hasFreeItem
                      ? 'Grátis'
                      : `${formattedItemValue.value} ${formattedItemValue.label}`}
                  </span>
                </VertTypography.Paragraph>
              </Col>
              {!isVirtual && (
                <Col className='item-track-shipping col'>
                  <VertTypography.Paragraph onClick={() => handleOpenModalTracking(vendor, item)}>
                    Rastrear
                  </VertTypography.Paragraph>
                </Col>
              )}

              <Col className='item-buttons col'>
                {!hasFreeItem && (
                  <VertButton
                    type='highlight'
                    onClick={() => goToProductDetailsPage(item.productSkuId)}
                    style={{
                      display: supplierWithoutShipping ? 'block' : 'none',
                    }}
                  >
                    Resgatar novamente
                  </VertButton>
                )}

                {!isLoading && isEnabled &&
                  checkIfOrderWas10DaysAgo() &&
                  ratingList &&
                  (!ratingList[item?.productSkuId] ||
                    ratingList[item?.productSkuId]?.status === 404) && (
                    <VertButton
                      type='default'
                      onClick={() => {
                        handleChangeSkuTarget(item?.productSkuId);
                        handleChangeModal(true);
                      }}
                    >
                      Avalie seu pedido
                    </VertButton>
                  )}
              </Col>
            </Row>
          </>
        );
      })}
      {showRatingProduct(vendor)}
    </Col>
  ));
};
